"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { FileSpreadsheet, FileText } from "lucide-react";
import { format } from "date-fns";
import { formatCurrency } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { exportToExcel, exportToPDF } from "@/lib/excelExport";

interface LossExceptionsDashboardProps {
  data: {
    dateRange: {
      start: string;
      end: string;
    };
    summary: {
      total: number;
      totalLostRevenue: number;
      avgLostPerCancellation: number;
    };
    byReason: {
      [reason: string]: number;
    };
    staffCancellations: Array<{
      name: string;
      count: number;
    }>;
    dayWiseTrend: Array<{
      date: string;
      cancellations: number;
    }>;
    details: Array<{
      appointmentId: string;
      date: string;
      customerName: string;
      vehiclePlate: string;
      staffName: string;
      lostRevenue: number;
    }>;
  };
}

export function LossExceptionsDashboard({ data }: LossExceptionsDashboardProps) {

  const COLORS = ["#ef4444", "#f59e0b", "#eab308", "#8b5cf6"];

  const getExportData = () => {
    return data.details.map((detail) => ({
      Date: format(new Date(detail.date), "dd/MM/yyyy"),
      Customer: detail.customerName,
      Vehicle: detail.vehiclePlate,
      Staff: detail.staffName,
      "Lost Revenue": detail.lostRevenue,
    }));
  };

  const handleExportExcel = () => {
    exportToExcel(getExportData(), "Losses_Exceptions_Report", "Cancellations");
  };

  const handleExportPDF = () => {
    exportToPDF(getExportData(), "Losses_Exceptions_Report", {
      title: "Losses & Exceptions Report",
      subtitle: "Cancellation details and lost revenue",
      dateRange: data.dateRange,
    });
  };

  const pieData = Object.entries(data.byReason).map(([name, value]) => ({
    name,
    value: Math.round(value),
  }));

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">
              Total Cancellations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {data.summary.total}
            </div>
            <p className="text-xs text-muted-foreground">
              In selected period
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Lost Revenue</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {formatCurrency(data.summary.totalLostRevenue)}
            </div>
            <p className="text-xs text-muted-foreground">
              Potential revenue lost
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Avg Lost per Cancellation</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(data.summary.avgLostPerCancellation)}
            </div>
            <p className="text-xs text-muted-foreground">
              Average value per cancellation
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Cancellation Reasons Pie Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Cancellation Reasons</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) =>
                    `${name}: ${(percent * 100).toFixed(0)}%`
                  }
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>

            <div className="space-y-2">
              <h4 className="font-medium mb-3">Top Cancellation Reasons</h4>
              {pieData
                .sort((a, b) => b.value - a.value)
                .map((item, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-2 border rounded"
                  >
                    <span className="text-sm">{item.name}</span>
                    <Badge variant="outline">
                      {item.value} ({data.summary.total > 0 ? Math.round((item.value / data.summary.total) * 100) : 0}%)
                    </Badge>
                  </div>
                ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Staff-wise Cancellations */}
      <Card>
        <CardHeader>
          <CardTitle>Staff-wise Cancellations</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.staffCancellations}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#ef4444" name="Cancellations" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Day-wise Trend */}
      <Card>
        <CardHeader>
          <CardTitle>Cancellation Trend</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={data.dayWiseTrend}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="cancellations"
                stroke="#ef4444"
                name="Cancellations"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Exception Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Cancellation Details</CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleExportExcel}>
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Excel
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportPDF}>
              <FileText className="h-4 w-4 mr-2" />
              PDF
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Vehicle</TableHead>
                  <TableHead>Staff</TableHead>
                  <TableHead className="text-right">Lost Revenue</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.details
                  .slice()
                  .reverse()
                  .map((detail) => (
                    <TableRow key={detail.appointmentId}>
                      <TableCell>
                        {format(new Date(detail.date), "MMM dd, yyyy")}
                      </TableCell>
                      <TableCell>{detail.customerName}</TableCell>
                      <TableCell>{detail.vehiclePlate}</TableCell>
                      <TableCell>{detail.staffName}</TableCell>
                      <TableCell className="text-right text-red-600">
                        {formatCurrency(detail.lostRevenue)}
                      </TableCell>
                    </TableRow>
                  ))}
                {data.details.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground">
                      No cancellations in this period
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

